import React, { useState, useEffect } from "react";
import "../FilterPanel/FilterPanel.css";
import "./DashboardFilterPanel.css";

const PANEL_STATUS_OPTIONS = [
    { value: "full", label: "Full Panel" },
    { value: "not-full", label: "Not Full Panel" },
];

const emptyDashFilters = {
    searchTerm: "",
    selectedColor: "",
    selectedProductType: "",
    fullPanelFilter: "",
    startDate: "",
    endDate: "",
};
 
function loadFromSession() {
    return {
        searchTerm:          localStorage.getItem("dashboard_filter_searchTerm")    || "",
        selectedColor:       localStorage.getItem("dashboard_filter_color")         || "", 
        selectedProductType: localStorage.getItem("dashboard_filter_productType")   || "",
        fullPanelFilter:     localStorage.getItem("dashboard_filter_fullPanel")     || "",
        startDate:           localStorage.getItem("dashboard_filter_startDate")     || "",
        endDate:             localStorage.getItem("dashboard_filter_endDate")       || "",
    };
}

function saveToSession(filters) {
    localStorage.setItem("dashboard_filter_searchTerm",  filters.searchTerm);
    localStorage.setItem("dashboard_filter_color",       filters.selectedColor);
    localStorage.setItem("dashboard_filter_productType", filters.selectedProductType);
    localStorage.setItem("dashboard_filter_fullPanel",   filters.fullPanelFilter);
    localStorage.setItem("dashboard_filter_startDate",   filters.startDate);
    localStorage.setItem("dashboard_filter_endDate",     filters.endDate);
}

function clearSession() {
    localStorage.removeItem("dashboard_filter_searchTerm");
    localStorage.removeItem("dashboard_filter_color");
    localStorage.removeItem("dashboard_filter_productType");
    localStorage.removeItem("dashboard_filter_fullPanel");
    localStorage.removeItem("dashboard_filter_startDate");
    localStorage.removeItem("dashboard_filter_endDate");
}

const FilterChip = ({ label, isSelected, onClick }) => (
    <button
        className={`fp-chip${isSelected ? " fp-chip--selected" : ""}`}
        onClick={onClick}
        type="button"
    >
        {label}
    </button>
);

const DashboardFilterPanel = ({
    isOpen,
    appliedFilters,
    onApply,
    productTypes = [],
    colors = [],
}) => {
    const [pendingFilters, setPendingFilters] = useState({ ...appliedFilters });

    useEffect(() => {
        setPendingFilters({ ...appliedFilters });
    }, [appliedFilters]);

    const set = (key) => (value) =>
        setPendingFilters((prev) => ({ ...prev, [key]: value }));

    const hasAppliedFilters =
        appliedFilters.searchTerm          !== "" ||
        appliedFilters.selectedColor       !== "" ||
        appliedFilters.selectedProductType !== "" ||
        appliedFilters.fullPanelFilter     !== "" ||
        appliedFilters.startDate           !== "" ||
        appliedFilters.endDate             !== "";

    const hasChanges =
        pendingFilters.searchTerm          !== appliedFilters.searchTerm          ||
        pendingFilters.selectedColor       !== appliedFilters.selectedColor       ||
        pendingFilters.selectedProductType !== appliedFilters.selectedProductType ||
        pendingFilters.fullPanelFilter     !== appliedFilters.fullPanelFilter     ||
        pendingFilters.startDate           !== appliedFilters.startDate           ||
        pendingFilters.endDate             !== appliedFilters.endDate;

    const handleApply = () => {
        if (!hasChanges) return;
        saveToSession(pendingFilters);
        onApply(pendingFilters);
    };

    const handleReset = () => {
        clearSession();
        const reset = { ...emptyDashFilters };
        setPendingFilters(reset);
        onApply(reset);
    };

    return (
        <div className={`filter-panel${isOpen ? "" : " filter-panel--collapsed"}`} data-tutorial="inventory-filters">
            <div className="filter-panel__inner">
                {/* Actions (sticky top) */}
                <div className="fp-actions">
                    <button
                        className="fp-btn-reset"
                        onClick={handleReset}
                        type="button"
                        disabled={!hasAppliedFilters}
                    >
                        Reset
                    </button>
                    <button
                        className="fp-btn-apply"
                        onClick={handleApply}
                        type="button"
                        disabled={!hasChanges}
                    >
                        Apply Filters
                    </button>
                </div>

                {/* Product name search */}
                <input
                    type="text"
                    className="fp-search"
                    placeholder="Search by product name"
                    value={pendingFilters.searchTerm}
                    onChange={(e) => set("searchTerm")(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleApply()}
                    data-tutorial="inventory-search"
                />

                {/* Color */}
                {colors.length > 0 && (
                    <>
                        <span className="fp-section-title">Color</span>
                        <div className="fp-chips">
                            {colors.map((color) => (
                                <FilterChip
                                    key={color}
                                    label={color.charAt(0).toUpperCase() + color.slice(1)}
                                    isSelected={pendingFilters.selectedColor === color}
                                    onClick={() =>
                                        set("selectedColor")(
                                            pendingFilters.selectedColor === color ? "" : color
                                        )
                                    }
                                />
                            ))}
                        </div>
                    </>
                )}

                {/* Product Type */}
                {productTypes.length > 0 && (
                    <>
                        <span className="fp-section-title">Product Type</span>
                        <div className="fp-chips">
                            {productTypes.map((type) => (
                                <FilterChip
                                    key={type}
                                    label={type.charAt(0).toUpperCase() + type.slice(1)}
                                    isSelected={pendingFilters.selectedProductType === type}
                                    onClick={() =>
                                        set("selectedProductType")(
                                            pendingFilters.selectedProductType === type ? "" : type
                                        )
                                    }
                                />
                            ))}
                        </div>
                    </>
                )}

                {/* Panel Status */}
                <span className="fp-section-title">Panel Status</span>
                <div className="fp-chips">
                    {PANEL_STATUS_OPTIONS.map((opt) => (
                        <FilterChip
                            key={opt.value}
                            label={opt.label}
                            isSelected={pendingFilters.fullPanelFilter === opt.value}
                            onClick={() =>
                                set("fullPanelFilter")(
                                    pendingFilters.fullPanelFilter === opt.value ? "" : opt.value
                                )
                            }
                        />
                    ))}
                </div>

                {/* Date Range */}
                <span className="fp-section-title">Date Range</span>
                <div className="dfp-date-row">
                    <input
                        type="date"
                        className="fp-range-input dfp-date-input"
                        value={pendingFilters.startDate}
                        onChange={(e) => set("startDate")(e.target.value)}
                        title="Start date"
                    />
                    <span className="fp-range-sep">–</span>
                    <input
                        type="date"
                        className="fp-range-input dfp-date-input"
                        value={pendingFilters.endDate}
                        onChange={(e) => set("endDate")(e.target.value)}
                        title="End date"
                    />
                </div>
                {(pendingFilters.startDate || pendingFilters.endDate) && (
                    <button
                        className="dfp-date-clear"
                        type="button"
                        onClick={() => {
                            set("startDate")("");
                            set("endDate")("");
                        }}
                    >
                        Clear dates
                    </button>
                )}

            </div>
        </div>
    );
};

export default DashboardFilterPanel;
